export const MENTIONS_STORAGE_KEY = "turbonotes2-mentions";
/** How many people the @mention list remembers, the most recently mentioned first. */
export const MAX_MENTIONS = 200;
export const MAX_MENTION_LENGTH = 64;
// A JSON list of the longest names: the account preference that stores it is this long at most.
export const MAX_MENTIONS_VALUE_LENGTH = MAX_MENTIONS * (MAX_MENTION_LENGTH + 3) + 2;

const NAME = /^[\p{L}\p{N}_](?:[\p{L}\p{N}_.-]*[\p{L}\p{N}_])?$/u;
// An @ after a letter, a dot, or a slash is an email address or a URL, not a mention. A name
// ends where no letter follows, so "@ana." mentions "ana", but "@ana@example.com" is no one.
const MENTION =
  /(?<![\p{L}\p{N}_@./])@([\p{L}\p{N}_](?:[\p{L}\p{N}_.-]*[\p{L}\p{N}_])?)(?![.-]*[\p{L}\p{N}_@])/gu;

export const isMention = (name: unknown): name is string =>
  typeof name === "string" && name.length <= MAX_MENTION_LENGTH && NAME.test(name);

/** Every @mention in a piece of text, with where its @ starts and its name ends. */
export function mentionRanges(text: string): { name: string; from: number; to: number }[] {
  return [...text.matchAll(MENTION)]
    .filter((match) => isMention(match[1]))
    .map((match) => ({ name: match[1]!, from: match.index, to: match.index + match[0].length }));
}

export interface MentionContext {
  openOffset: number;
  query: string;
}

/** Find an unfinished @mention immediately before the cursor. */
export function getMentionContext(textBeforeCursor: string): MentionContext | null {
  const match = /(?<![\p{L}\p{N}_@./])@([\p{L}\p{N}_.-]*)$/u.exec(textBeforeCursor);
  if (!match || (match[1] ?? "").length > MAX_MENTION_LENGTH) return null;
  return { openOffset: match.index, query: match[1] ?? "" };
}

/**
 * The remembered names that fit what has been typed after the @: those that start with it
 * first, each group most recent first. A name typed out in full is left off, so Enter after it
 * starts a new line instead of picking it again.
 */
export function filterMentions(
  names: readonly string[],
  query: string,
  limit = 8,
): readonly string[] {
  const typed = query.toLocaleLowerCase();
  const fits = names.filter((name) => name !== query && name.toLocaleLowerCase().includes(typed));
  return [
    ...fits.filter((name) => name.toLocaleLowerCase().startsWith(typed)),
    ...fits.filter((name) => !name.toLocaleLowerCase().startsWith(typed)),
  ].slice(0, limit);
}

/** The stored list, or none if it is missing or damaged. */
export function parseMentions(value: string | null): string[] {
  if (!value) return [];
  try {
    const names: unknown = JSON.parse(value);
    return Array.isArray(names) ? names.filter(isMention).slice(0, MAX_MENTIONS) : [];
  } catch {
    return [];
  }
}

export function isMentionList(value: string): boolean {
  try {
    const names: unknown = JSON.parse(value);
    return Array.isArray(names) && names.length <= MAX_MENTIONS && names.every(isMention);
  } catch {
    return false;
  }
}

/** The list with `names` moved to the front. Case doesn't make a second person. */
export function rememberMentions(list: readonly string[], names: readonly string[]): string[] {
  const seen = new Set<string>();
  return [...names.filter(isMention), ...list]
    .filter((name) => {
      const key = name.toLocaleLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, MAX_MENTIONS);
}
